import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Beaker } from "lucide-react";
import { api } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

export default function Ingredients() {
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    api.products().then((d) => setIngredients(d.ingredients || [])).catch(() => {});
  }, []);

  return (
    <div className="max-w-site mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="ingredients-index">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
        <div className="md:col-span-4"><SectionLabel index="The Library">Every active, in depth.</SectionLabel></div>
        <div className="md:col-span-8">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.98]">
            The Vitura Performance™ Blend, <span className="editorial normal-case">ingredient by ingredient.</span>
          </h1>
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
            Fifteen clinical actives inside one 2,008 mg blend. Explore the mechanism, benefit and research behind each one.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[color:var(--line)]">
        {ingredients.map((ing, i) => (
          <motion.div
            key={ing.slug}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
            className="border-b border-r border-[color:var(--line)]"
          >
            <Link
              to={`/ingredients/${ing.slug}`}
              className="group block p-7 h-full hover:bg-[color:var(--bg-soft)] transition"
              data-testid={`ingredient-card-${ing.slug}`}
            >
              <div className="flex items-center justify-between">
                <span className="label !text-[10px] !text-[color:var(--ink-3)]">{ing.category}</span>
                {ing.show_dose ? (
                  <span className="font-mono tabular-nums text-[12px] font-semibold px-2 py-0.5 bg-[#a8c69f]/30 text-[#3a4a32] border border-[#a8c69f]/70">{ing.dose}</span>
                ) : (
                  <span className="inline-flex items-center justify-center w-6 h-6 border border-[color:var(--line)] text-[color:var(--ink-3)]" title="Measured within the Vitura Performance™ Blend">
                    <Beaker size={12} strokeWidth={1.5} />
                  </span>
                )}
              </div>
              <h2 className="font-display text-xl md:text-2xl tracking-[-0.01em] leading-tight mt-4">{ing.name}</h2>
              <p className="text-[14px] text-[color:var(--ink-2)] mt-2 leading-snug">{ing.benefit}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 label !text-[10px] !tracking-[0.16em] group-hover:opacity-60 transition">
                Explore <ArrowUpRight size={12} strokeWidth={1.6} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
